import React, { useEffect, useMemo, useRef, useState } from "react";
import { setReturnTo } from "../lib/returnTo";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Download, FileText, Lock } from "lucide-react";
import AnalystBroNavbar from "../components/AnalystBroNavbar";
import AnalystBroFooter from "../components/AnalystBroFooter";
import StockSearch from "../components/StockSearch";
import { useAuth } from "../contexts/AuthContext";
import { articleXlsxUrl, fileUrl, getReports, getSummary, getTab, signedReportUrl } from "../lib/stockApi";

// Public symbol page (equity / ETF): /symbols/EXCHANGE:SYMBOL[/<report-kind>[/<period>]] (backend plan 285).
// Each tab loads its own payload when opened. The server also renders crawlable HTML for
// these URLs; this page replaces it once the app loads.

const THEME = "ab-light";
const SITE = "https://analystbro.net";
const TABS = [
  ["overview", "Overview"],
  ["financials", "Financials"],
  ["statistics", "Statistics"],
  ["dividends", "Dividends"],
  ["profile", "Profile"],
  ["history", "History"],
  ["reports", "Reports"],
];
const STATEMENTS = [
  ["income", "Income"],
  ["balance", "Balance Sheet"],
  ["cashflow", "Cash Flow"],
  ["ratios", "Ratios"],
];
const KEY_ROWS = 12;
const REPORT_KINDS = new Set([
  "annual-reports", "quarterly-reports", "financial-statements", "price-sensitive-information",
  "credit-ratings", "regulatory-disclosures", "esg-reports",
]);

const card = "rounded-xl border border-[var(--ab-border)] bg-[var(--ab-surface)]";
const h2 = "font-display text-xl font-bold tracking-tight";
const muted = "text-[var(--ab-muted)]";

// ── formatting ──────────────────────────────────────────────────────────────
function abbrev(n) {
  const a = Math.abs(n);
  if (a >= 1e12) return (n / 1e12).toFixed(2) + "T";
  if (a >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (a >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (a >= 1e4) return (n / 1e3).toFixed(1) + "K";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmt(v, unit) {
  if (v === null || v === undefined || v === "") return "–";
  if (typeof v !== "number") return String(v);
  if (unit === "fraction") return (v * 100).toFixed(2) + "%";
  if (unit === "currency") return abbrev(v);
  if (unit === "currency_per_share" || unit === "ratio") return v.toFixed(2);
  return abbrev(v);
}
const pct = (v) => (typeof v === "number" ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}%` : "");
const title = (k) => k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

function useHead({ t, description, canonical }) {
  useEffect(() => {
    if (!t) return;
    const prev = document.title;
    document.title = t;
    const set = (sel, make, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = make();
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    set('meta[name="description"]', () => Object.assign(document.createElement("meta"), { name: "description" }), "content", description || "");
    set('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" }), "href", canonical);
    return () => {
      document.title = prev;
    };
  }, [t, description, canonical]);
}

// Fetch helper with loading / error state.
function useLoad(fn, deps) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let live = true;
    setState({ loading: true, data: null, error: null });
    fn()
      .then((data) => live && setState({ loading: false, data, error: null }))
      .catch((error) => live && setState({ loading: false, data: null, error }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

const Skeleton = ({ rows = 6 }) => (
  <div className="animate-pulse space-y-2" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-4 rounded bg-[var(--ab-bg-alt)]" style={{ width: `${90 - (i % 3) * 15}%` }} />
    ))}
  </div>
);
const Missing = ({ what = "This data" }) => (
  <p className={`text-sm ${muted}`}>{what} is not available for this symbol right now.</p>
);
function Section({ state, what, children }) {
  if (state.loading) return <Skeleton />;
  if (state.error || !state.data) return <Missing what={what} />;
  return children(state.data);
}

// ── chart ───────────────────────────────────────────────────────────────────
function PriceChart({ bars }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !bars?.length) return;
    let chart;
    let gone = false;
    import("lightweight-charts").then(({ createChart, AreaSeries }) => {
      if (gone || !ref.current) return;
      const css = getComputedStyle(ref.current);
      const accent = css.getPropertyValue("--ab-accent").trim() || "#2F6FE4";
      chart = createChart(ref.current, {
        autoSize: true,
        layout: { background: { color: "transparent" }, textColor: css.getPropertyValue("--ab-muted").trim() || "#5B6474", attributionLogo: false },
        grid: { vertLines: { visible: false }, horzLines: { color: css.getPropertyValue("--ab-border").trim() || "#E3E7ED" } },
        rightPriceScale: { borderVisible: false },
        timeScale: { borderVisible: false },
        handleScroll: false,
        handleScale: false,
      });
      const s = chart.addSeries(AreaSeries, {
        lineColor: accent, topColor: accent + "33", bottomColor: accent + "00", lineWidth: 2,
      });
      s.setData([...bars].reverse().map((b) => ({ time: b.date, value: b.close })));
      chart.timeScale().fitContent();
    });
    return () => {
      gone = true;
      chart?.remove();
    };
  }, [bars]);
  return <div ref={ref} className="h-64 sm:h-80 w-full" />;
}

// ── tabs ────────────────────────────────────────────────────────────────────
function Overview({ exsym, summary, reports, onTab }) {
  const ov = useLoad(() => getTab(exsym, "overview"), [exsym]);
  const hist = useLoad(() => getTab(exsym, "history"), [exsym]);
  const STATS = [
    ["market_cap", "Market cap"], ["revenue_ttm", "Revenue (TTM)"], ["net_income_ttm", "Net income (TTM)"],
    ["shares_out", "Shares out"], ["eps_ttm", "EPS (TTM)"], ["pe_ratio", "P/E"], ["forward_pe", "Forward P/E"],
    ["dividend", "Dividend"], ["ex_dividend_date", "Ex-dividend date"], ["beta", "Beta"],
    ["change_1y", "1-year change"], ["earnings_date", "Earnings date"],
  ];
  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <div className="space-y-6 min-w-0">
        <div className={`${card} p-4 sm:p-5`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={h2}>Price</h2>
            <button className="text-sm font-semibold text-[var(--ab-accent)]" onClick={() => onTab("history")}>History →</button>
          </div>
          {hist.loading ? <div className="h-64 animate-pulse rounded bg-[var(--ab-bg-alt)]" /> :
            hist.data?.bars?.length ? <PriceChart bars={hist.data.bars} /> : <Missing what="Price history" />}
        </div>
        <div className={`${card} p-4 sm:p-5`}>
          <h2 className={`${h2} mb-3`}>Key stats</h2>
          <Section state={ov} what="Key statistics">
            {(d) => (
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                {[["low_52w", "52-week low"], ["high_52w", "52-week high"], ...STATS].map(([k, label]) =>
                  d.stats?.[k] !== undefined ? (
                    <div key={k} className="min-w-0">
                      <dt className={`text-xs ${muted}`}>{label}</dt>
                      <dd className="font-semibold tabular-nums truncate">{String(d.stats[k])}</dd>
                    </div>
                  ) : null
                )}
              </dl>
            )}
          </Section>
        </div>
      </div>
      <div className="space-y-6 min-w-0">
        <div className={`${card} p-4 sm:p-5`}>
          <h2 className={`${h2} mb-2`}>About</h2>
          <Section state={ov} what="The company description">
            {(d) => (
              <>
                <p className={`text-sm leading-relaxed ${muted}`}>{d.description || summary.description || "–"}</p>
                {d.facts?.length > 0 && (
                  <dl className="mt-3 space-y-1 text-sm">
                    {d.facts.map((f) => (
                      <div key={f.title} className="flex justify-between gap-3">
                        <dt className={muted}>{f.title}</dt>
                        <dd className="font-medium text-right">{String(f.value)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <button className="mt-3 text-sm font-semibold text-[var(--ab-accent)]" onClick={() => onTab("profile")}>Full profile →</button>
              </>
            )}
          </Section>
        </div>
        {reports.data?.reports?.length > 0 && (
          <div className={`${card} p-4 sm:p-5`}>
            <h2 className={`${h2} mb-2`}>Latest reports</h2>
            <ul className="space-y-2 text-sm">
              {reports.data.reports.filter((r) => r.public).slice(0, 4).map((r) => (
                <li key={r.id}>
                  <a href={fileUrl(r.file_url)} target="_blank" rel="noreferrer" className="flex gap-2 items-start hover:text-[var(--ab-accent)]">
                    <FileText size={16} className="mt-0.5 shrink-0 text-[var(--ab-accent)]" />
                    <span>{r.display_title || `${r.kind_label} ${r.period}`}{r.page_count ? ` · ${r.page_count} pages` : ""}</span>
                  </a>
                </li>
              ))}
            </ul>
            <button className="mt-3 text-sm font-semibold text-[var(--ab-accent)]" onClick={() => onTab("reports")}>All reports →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatementTable({ data }) {
  const [all, setAll] = useState(false);
  const rows = data.rows || [];
  const shown = all ? rows : rows.slice(0, KEY_ROWS);
  return (
    <>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="min-w-full text-sm tabular-nums">
          <thead>
            <tr className="border-b border-[var(--ab-border)]">
              <th className="sticky left-0 bg-[var(--ab-surface)] text-left font-semibold py-2 px-4 sm:pl-0 min-w-[11rem]">
                {data.currency ? `${data.currency}` : ""}
              </th>
              {data.columns.map((c, i) => (
                <th key={i} className="text-right font-semibold py-2 px-3 whitespace-nowrap" title={data.period_end?.[i] || ""}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-[var(--ab-border)] last:border-0">
                <td className={`sticky left-0 bg-[var(--ab-surface)] py-2 px-4 sm:pl-0 ${r.total ? "font-semibold" : muted}`}>{r.title}</td>
                {data.columns.map((_, i) => (
                  <td key={i} className={`text-right py-2 px-3 whitespace-nowrap ${r.total ? "font-semibold" : ""}`}>{fmt(r.values[i], r.unit)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > KEY_ROWS && (
        <button onClick={() => setAll((v) => !v)} className="mt-3 text-sm font-semibold text-[var(--ab-accent)]">
          {all ? "Show key rows" : `Show all ${rows.length} rows`}
        </button>
      )}
      <p className={`mt-3 text-xs ${muted}`}>
        Columns newest first; TTM = trailing twelve months. Hover a column for its period end.
      </p>
    </>
  );
}

const Toggle = ({ options, value, onChange }) => (
  <div className="inline-flex rounded-lg border border-[var(--ab-border)] p-0.5 bg-[var(--ab-bg)]">
    {options.map(([k, label]) => (
      <button
        key={k}
        onClick={() => onChange(k)}
        className={`px-3 py-1.5 rounded-md text-sm font-medium ${value === k ? "bg-[var(--ab-surface)] text-[var(--ab-text)] shadow-sm" : muted}`}
      >
        {label}
      </button>
    ))}
  </div>
);

function Financials({ exsym, statement, period, setParam }) {
  const st = useLoad(() => getTab(exsym, statement, period), [exsym, statement, period]);
  return (
    <div className={`${card} p-4 sm:p-5`}>
      <div className="flex flex-wrap gap-3 justify-between mb-4">
        <div className="overflow-x-auto"><Toggle options={STATEMENTS} value={statement} onChange={(v) => setParam("statement", v)} /></div>
        <Toggle options={[["annual", "Annual"], ["quarterly", "Quarterly"]]} value={period} onChange={(v) => setParam("period", v)} />
      </div>
      <Section state={st} what="This statement">{(d) => <StatementTable data={d} />}</Section>
    </div>
  );
}

function Statistics({ exsym }) {
  const st = useLoad(() => getTab(exsym, "statistics"), [exsym]);
  return (
    <Section state={st} what="Statistics">
      {(d) => (
        <div className="grid gap-6 md:grid-cols-2">
          {Object.entries(d.groups || {}).map(([g, items]) => (
            <div key={g} className={`${card} p-4 sm:p-5`}>
              <h2 className={`${h2} mb-3`}>{title(g)}</h2>
              <dl className="text-sm divide-y divide-[var(--ab-border)]">
                {items.map((it) => (
                  <div key={it.title} className="flex justify-between gap-4 py-1.5">
                    <dt className={muted}>{it.title}</dt>
                    <dd className="font-medium tabular-nums text-right">{String(it.value ?? "–")}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Dividends({ exsym }) {
  const st = useLoad(() => getTab(exsym, "dividends"), [exsym]);
  return (
    <Section state={st} what="Dividend data">
      {(d) => (
        <div className="space-y-6">
          {Object.keys(d.summary || {}).length > 0 && (
            <div className={`${card} p-4 sm:p-5`}>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(d.summary).map(([k, v]) => (
                  <div key={k}><dt className={`text-xs ${muted}`}>{title(k)}</dt><dd className="font-semibold">{String(v)}</dd></div>
                ))}
              </dl>
              {d.note && <p className={`mt-3 text-sm ${muted}`}>{d.note}</p>}
            </div>
          )}
          <div className={`${card} p-4 sm:p-5 overflow-x-auto`}>
            {d.history?.length ? (
              <table className="min-w-full text-sm tabular-nums">
                <thead><tr className="border-b border-[var(--ab-border)] text-left">
                  {["Ex-date", "Amount", "Record date", "Pay date", "Declared"].map((h) => <th key={h} className="py-2 pr-4 font-semibold whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {d.history.map((h, i) => (
                    <tr key={i} className="border-b border-[var(--ab-border)] last:border-0">
                      <td className="py-2 pr-4 whitespace-nowrap">{h.ex_date}</td>
                      <td className="py-2 pr-4 font-medium">{h.amount_text ?? h.amount}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{h.record_date || "–"}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{h.pay_date || "–"}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{h.declared || "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <Missing what="Dividend history" />}
          </div>
        </div>
      )}
    </Section>
  );
}

function Profile({ exsym }) {
  const st = useLoad(() => getTab(exsym, "profile"), [exsym]);
  return (
    <Section state={st} what="The company profile">
      {(d) => (
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6 min-w-0">
            <div className={`${card} p-4 sm:p-5`}>
              <h2 className={`${h2} mb-2`}>Description</h2>
              {(d.description || "–").split("\n").map((p, i) => <p key={i} className={`text-sm leading-relaxed ${muted} mb-2`}>{p}</p>)}
            </div>
            {d.executives?.length > 0 && (
              <div className={`${card} p-4 sm:p-5`}>
                <h2 className={`${h2} mb-2`}>Key executives</h2>
                <ul className="text-sm divide-y divide-[var(--ab-border)]">
                  {d.executives.map((e, i) => (
                    <li key={i} className="flex justify-between gap-4 py-1.5"><span className="font-medium">{e.name}</span><span className={`${muted} text-right`}>{e.title}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {d.filings?.length > 0 && (
              <div className={`${card} p-4 sm:p-5`}>
                <h2 className={`${h2} mb-2`}>Recent filings</h2>
                <ul className="text-sm divide-y divide-[var(--ab-border)]">
                  {d.filings.map((f, i) => (
                    <li key={i} className="py-1.5 flex gap-3">
                      <span className={`${muted} whitespace-nowrap`}>{f.date}</span>
                      {f.url ? <a href={f.url} target="_blank" rel="noreferrer" className="hover:text-[var(--ab-accent)]">{f.title || f.type}</a> : <span>{f.title || f.type}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="space-y-6 min-w-0">
            <div className={`${card} p-4 sm:p-5`}>
              <h2 className={`${h2} mb-2`}>Details</h2>
              <dl className="text-sm divide-y divide-[var(--ab-border)]">
                {Object.entries(d.details || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-1.5"><dt className={muted}>{title(k.replace(/_/g, " "))}</dt><dd className="font-medium text-right">{String(v)}</dd></div>
                ))}
              </dl>
            </div>
            {Object.keys(d.contact || {}).length > 0 && (
              <div className={`${card} p-4 sm:p-5`}>
                <h2 className={`${h2} mb-2`}>Contact</h2>
                <dl className="text-sm space-y-1">
                  {d.contact.address && <dd>{d.contact.address}</dd>}
                  {d.contact.phone && <dd>{d.contact.phone}</dd>}
                  {d.contact.website && (
                    <dd><a className="text-[var(--ab-accent)] break-all" href={d.contact.website.startsWith("http") ? d.contact.website : `https://${d.contact.website}`} target="_blank" rel="noreferrer nofollow">{d.contact.website}</a></dd>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function History({ exsym }) {
  const st = useLoad(() => getTab(exsym, "history"), [exsym]);
  return (
    <Section state={st} what="Price history">
      {(d) => (
        <div className="space-y-6">
          <div className={`${card} p-4 sm:p-5`}><PriceChart bars={d.bars} /></div>
          <div className={`${card} p-4 sm:p-5 overflow-x-auto`}>
            <table className="min-w-full text-sm tabular-nums">
              <thead><tr className="border-b border-[var(--ab-border)]">
                {["Date", "Open", "High", "Low", "Close", "Change", "Volume"].map((h, i) => <th key={h} className={`py-2 px-2 font-semibold ${i ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {d.bars.map((b) => (
                  <tr key={b.date} className="border-b border-[var(--ab-border)] last:border-0">
                    <td className="py-1.5 px-2 whitespace-nowrap">{b.date}</td>
                    <td className="py-1.5 px-2 text-right">{fmt(b.open, "ratio")}</td>
                    <td className="py-1.5 px-2 text-right">{fmt(b.high, "ratio")}</td>
                    <td className="py-1.5 px-2 text-right">{fmt(b.low, "ratio")}</td>
                    <td className="py-1.5 px-2 text-right font-medium">{fmt(b.close, "ratio")}</td>
                    <td className={`py-1.5 px-2 text-right ${b.change_pct < 0 ? "text-red-600" : "text-[var(--ab-up)]"}`}>{pct(b.change_pct)}</td>
                    <td className="py-1.5 px-2 text-right">{fmt(b.volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Section>
  );
}

function ReportButton({ r }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const btn = "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap";
  if (r.public) {
    return (
      <a href={fileUrl(r.file_url)} target="_blank" rel="noreferrer" className={`${btn} bg-[var(--ab-accent)] text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)]`}>
        <Download size={14} /> View PDF
      </a>
    );
  }
  if (!isAuthenticated) {
    return (
      <button
        onClick={() => {
          const back = location.pathname + location.search;
          setReturnTo(back);
          navigate(`/login?next=${encodeURIComponent(back)}`, { state: { from: { pathname: back } } });
        }}
        className={`${btn} border border-[var(--ab-border)] text-[var(--ab-text)] hover:border-[var(--ab-accent)]`}
      >
        <Lock size={14} /> Sign in to download
      </button>
    );
  }
  return (
    <button
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        const w = window.open("", "_blank");
        try {
          const url = await signedReportUrl(r.id);
          if (w) w.location.href = url;
          else window.location.href = url;
        } catch {
          w?.close();
        } finally {
          setBusy(false);
        }
      }}
      className={`${btn} border border-[var(--ab-border)] text-[var(--ab-text)] hover:border-[var(--ab-accent)]`}
    >
      <Download size={14} /> {busy ? "Opening…" : "View PDF"}
    </button>
  );
}

function Reports({ exsym, reports, kind, period }) {
  const kinds = useMemo(() => {
    const seen = new Map();
    (reports.data?.reports || []).forEach((r) => seen.set(r.kind, r.kind_label));
    return [...seen.entries()];
  }, [reports.data]);
  const rows = (reports.data?.reports || []).filter((r) => (!kind || r.kind === kind) && (!period || r.period === period));
  return (
    <Section state={reports} what="Reports">
      {() => (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Link to={`/symbols/${exsym}/reports`} className={`px-3 py-1.5 rounded-lg text-sm border ${!kind ? "border-[var(--ab-accent)] text-[var(--ab-accent)]" : "border-[var(--ab-border)]"}`}>All</Link>
            {kinds.map(([k, label]) => (
              <Link key={k} to={`/symbols/${exsym}/${k}`} className={`px-3 py-1.5 rounded-lg text-sm border ${kind === k ? "border-[var(--ab-accent)] text-[var(--ab-accent)]" : "border-[var(--ab-border)]"}`}>{label}s</Link>
            ))}
          </div>
          {period && <p className="text-sm">Showing {period}. <Link className="text-[var(--ab-accent)]" to={`/symbols/${exsym}/${kind}`}>All periods</Link></p>}
          {rows.length === 0 ? <Missing what="Reports of this kind" /> : (
            <ul className={`${card} divide-y divide-[var(--ab-border)]`}>
              {rows.map((r) => (
                <li key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="min-w-0">
                    <Link to={r.path} className="font-semibold hover:text-[var(--ab-accent)]">{r.display_title || `${r.kind_label} ${r.period}`}</Link>
                    <p className={`text-sm ${muted} truncate`}>
                      {r.title}{r.page_count ? ` · ${r.page_count} pages` : ""}{r.file_size ? ` · ${(r.file_size / 1e6).toFixed(1)} MB` : ""}
                      {r.public ? " · latest, free to view" : ""}
                    </p>
                  </div>
                  <ReportButton r={r} />
                </li>
              ))}
            </ul>
          )}
          <p className={`text-xs ${muted}`}>The latest report of each kind is free to view. Sign in to download older reports.</p>
        </div>
      )}
    </Section>
  );
}

// ── page ────────────────────────────────────────────────────────────────────
function NotFound({ raw, choices }) {
  return (
    <div className="max-w-xl mx-auto py-16 space-y-6">
      <h1 className="font-display text-3xl font-bold">{choices ? `Which ${raw}?` : "Symbol not found"}</h1>
      {choices ? (
        <ul className={`${card} divide-y divide-[var(--ab-border)]`}>
          {choices.map((c) => (
            <li key={c.exsym}><Link to={c.path} className="flex justify-between gap-3 p-4 hover:bg-[var(--ab-accent-soft)]"><span className="font-semibold">{c.name}</span><span className="font-mono text-sm">{c.exsym}</span></Link></li>
          ))}
        </ul>
      ) : (
        <p className={muted}>We could not find “{raw}”. Search for a company or ticker:</p>
      )}
      <StockSearch autoFocus />
    </div>
  );
}

// Tabs are path URLs (/symbols/DSE:BATBC/dividends): each has its own title and server-rendered page for
// search engines. The old ?tab= form redirects here (the server 301s it too).
const TAB_SLUGS = new Set(["financials", "statistics", "dividends", "profile", "history", "reports", "technical", "research", "announcements"]);
const TAB_TITLES = {
  financials: "Financial Statements: Income, Balance Sheet, Cash Flow",
  statistics: "Key Statistics and Valuation Ratios",
  dividends: "Dividend History and Yield",
  profile: "Company Profile",
  history: "Price History",
  reports: "Annual and Quarterly Reports",
};

function parsePath(pathname) {
  const parts = pathname.replace(/^\/(?:symbols|stocks)\/?/, "").split("/").filter(Boolean).map((p) => {
    try { return decodeURIComponent(p); } catch { return p; }
  });
  return {
    raw: parts[0] || "",
    kind: REPORT_KINDS.has(parts[1]) ? parts[1] : null,
    period: parts[2] || null,
    pathTab: !parts[2] && TAB_SLUGS.has(parts[1]) ? parts[1] : null,
  };
}

// The technical report is rendered by the server (backend plan 300, api/market_pages/technical.py). Inside the
// symbol page we show that HTML under the page's own header and tabs: the HTML the server put in #root on first
// load (captured in main.jsx), otherwise fetched; its own heading is dropped (the page header names the stock).
function serverMain(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  doc.querySelectorAll(".abp-nav, main.abm > .abp-ex, main.abm > h1").forEach((el) => el.remove());
  return { html: doc.body.firstChild.innerHTML, title: null };
}

// Server-rendered tabs (technical report, research list; articles themselves live under /markets/<cc>/research): the HTML the server put in #root on first load
// (captured in main.jsx), otherwise fetched with the same query string (e.g. ?preview= for drafts).
function ServerTab({ url, what }) {
  const ssr = window.__SSR_ROOT__ && window.__SSR_ROOT__.url === url ? window.__SSR_ROOT__.html : null;
  const [state, setState] = useState(() => (ssr ? { html: serverMain(ssr).html, loading: false } : { html: "", loading: true }));
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const box = useRef(null);
  useEffect(() => {
    if (ssr) {
      window.__SSR_ROOT__ = null;   // used once; later visits fetch
      return undefined;
    }
    let cancelled = false;
    setState({ html: "", loading: true });
    fetch(url + window.location.search, { headers: { Accept: "text/html" } })
      .then((r) => r.text().then((t) => ({ ok: r.ok, t })))
      .then(({ ok, t }) => {
        if (cancelled) return;
        const doc = new DOMParser().parseFromString(t, "text/html");
        const root = doc.getElementById("root");
        if (doc.title) document.title = doc.title;
        setState({ html: ok && root ? serverMain(root.innerHTML).html : "", loading: false });
      })
      .catch(() => !cancelled && setState({ html: "", loading: false }));
    return () => { cancelled = true; };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  // "Download .xlsx" in an article (paid accounts): ask the API for a signed link instead of following the href.
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const onClick = async (e) => {
      const a = e.target.closest("[data-xlsx-download]");
      if (!a) return;
      e.preventDefault();
      if (!isAuthenticated) {
        setReturnTo(window.location.pathname);
        navigate("/login");
        return;
      }
      try {
        window.location.href = await articleXlsxUrl(window.location.pathname);
      } catch (err) {
        if (err?.response?.status === 402) navigate("/pricing");
      }
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [state.html, isAuthenticated, navigate]);
  if (state.loading) return <Skeleton rows={6} />;
  if (!state.html) return <Missing what={what} />;
  return <div ref={box} className="ab-technical -mx-4 sm:-mx-8" dangerouslySetInnerHTML={{ __html: state.html }} />;
}

export default function StockPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { raw, kind, period: reportPeriod, pathTab } = parsePath(location.pathname);
  const summary = useLoad(() => (raw ? getSummary(raw) : Promise.reject(new Error("empty"))), [raw]);
  const exsym = summary.data?.exsym;
  const reports = useLoad(() => (exsym ? getReports(exsym) : new Promise(() => {})), [exsym]);
  const tab = kind ? "reports" : pathTab || "overview";
  const legacyTab = !kind && !pathTab && TAB_SLUGS.has(params.get("tab")) ? params.get("tab") : null;
  const statement = params.get("statement") || "income";
  const period = params.get("period") === "quarterly" ? "quarterly" : "annual";

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    p.set(k, v);
    setParams(p, { replace: true });
  };
  const navigate = useNavigate();
  const onTab = (t) => navigate(`/symbols/${exsym}${t === "overview" ? "" : `/${t}`}`);

  const s = summary.data;
  const name = s?.name;
  const reportLabel = kind ? (reports.data?.reports || []).find((r) => r.kind === kind)?.kind_label : null;
  const isEtf = (s?.type || "").toLowerCase() === "etf";
  const pageTitle = !s ? "AnalystBro" : kind
    ? `${name} (${s.symbol}) ${reportLabel || "Report"}${reportPeriod ? ` ${reportPeriod}` : "s"} — AnalystBro`
    : TAB_TITLES[tab]
      ? `${name} (${s.symbol}) ${TAB_TITLES[tab]} — AnalystBro`
    : tab === "technical"
      ? `${name} (${s.symbol}) Technical Analysis: Trend, Levels, Charts — AnalystBro`
    : tab === "research"
      ? `${name} (${s.symbol}) Research and Valuation Reports — AnalystBro`
    : isEtf
      ? `${name} (${s.symbol}) — Price, Holdings & Reports — AnalystBro`
      : `${name} (${s.symbol}) Stock Price, Financials & Reports — AnalystBro`;
  useHead({
    t: s && pageTitle,
    description: s && (isEtf
      ? `${name} (${s.exsym}) ETF: price, key statistics, dividends, fund profile and reports on AnalystBro.`
      : `${name} (${s.exsym}): price, key statistics, financials, dividends, company profile and reports on AnalystBro.`),
    canonical: s && `${SITE}${location.pathname.replace(/^\/(?:symbols|stocks)\/[^/]+/, `/symbols/${s.exsym}`)}`,
  });

  if (!raw) {
    return (
      <Shell>
        <div className="max-w-xl mx-auto py-16 space-y-4">
          <h1 className="font-display text-3xl font-bold">Symbols</h1>
          <p className={muted}>Search a company, ETF or ticker to open its page.</p>
          <StockSearch autoFocus />
        </div>
      </Shell>
    );
  }
  // Canonical URL comes from the server (`exsym`): /symbols/dse:gp, /symbols/DSE:GP, /symbols/GP -> /symbols/DSEBD:GP
  // once Stock-Screener SYMBOL_PAGES_TV_FORM is on (doc 317); the server also 301s old URLs for crawlers.
  if (s && legacyTab) {
    const p = new URLSearchParams(params);
    p.delete("tab");
    const qs = p.toString();
    return <Navigate to={`/symbols/${s.exsym}/${legacyTab}${qs ? `?${qs}` : ""}`} replace />;
  }
  if (s && raw !== s.exsym) {
    const rest = location.pathname.split("/").slice(3).join("/");
    return <Navigate to={`/symbols/${s.exsym}${rest ? `/${rest}` : ""}${location.search}`} replace />;
  }

  const q = s?.quote || {};
  const up = (q.change ?? 0) >= 0;
  return (
    <Shell>
      {summary.loading ? (
        <div className="py-10"><Skeleton rows={3} /></div>
      ) : summary.error ? (
        <NotFound raw={raw} choices={summary.error.status === 409 ? summary.error.body?.choices : null} />
      ) : (
        <div className="py-6 sm:py-10 space-y-6">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--ab-accent)]">{s.exsym}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{name}</h1>
              <p className={`text-sm ${muted} mt-1`}>{[s.sector, s.industry, s.country].filter(Boolean).join(" · ")}</p>
            </div>
            {q.price !== undefined && (
              <div className="md:text-right">
                <div className="flex md:justify-end items-baseline gap-3">
                  <span className="font-display text-3xl font-bold tabular-nums">{q.price}</span>
                  <span className="text-sm font-medium">{s.currency}</span>
                  <span className={`font-semibold tabular-nums ${up ? "text-[var(--ab-up)]" : "text-red-600"}`}>
                    {q.change !== undefined ? `${up ? "+" : ""}${q.change.toFixed(2)}` : ""} {pct(q.change_pct)}
                  </span>
                </div>
                <p className={`text-xs ${muted}`}>{q.as_of}{s.market_cap ? ` · Market cap ${s.market_cap}` : ""}</p>
              </div>
            )}
          </header>

          <nav className="flex gap-1 overflow-x-auto border-b border-[var(--ab-border)] -mx-4 px-4 sm:mx-0 sm:px-0" aria-label="Symbol sections">
            {/* Real links (crawlers that run JavaScript follow <a href>, not buttons). Technical: DSE only so far. */}
            {[...TABS, ...(exsym?.startsWith("DSE:") ? [["announcements", "Announcements"], ["technical", "Technical"], ["research", "Research"]] : [])].map(([k, label]) => (
              <Link
                key={k}
                to={`/symbols/${exsym}${k === "overview" ? "" : `/${k}`}`}
                aria-current={tab === k ? "page" : undefined}
                className={`px-3 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-[var(--ab-accent)] text-[var(--ab-text)]" : `border-transparent ${muted} hover:text-[var(--ab-text)]`}`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {tab === "overview" && <Overview exsym={exsym} summary={s} reports={reports} onTab={onTab} />}
          {tab === "financials" && <Financials exsym={exsym} statement={statement} period={period} setParam={setParam} />}
          {tab === "statistics" && <Statistics exsym={exsym} />}
          {tab === "dividends" && <Dividends exsym={exsym} />}
          {tab === "profile" && <Profile exsym={exsym} />}
          {tab === "history" && <History exsym={exsym} />}
          {tab === "reports" && <Reports exsym={exsym} reports={reports} kind={kind} period={reportPeriod} />}
          {tab === "technical" && <ServerTab url={`/symbols/${exsym}/technical`} what="A technical report for this stock" />}
          {tab === "announcements" && (
            <ServerTab url={`/symbols/${exsym}/announcements`} what="Stock exchange announcements for this stock" />
          )}
          {tab === "research" && (
            <ServerTab url={`/symbols/${exsym}/research`} what="Research on this stock" />
          )}

          <section className={`${card} p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
            <div className="flex flex-col gap-2">
              <h2 className={h2}>Ask about {s.symbol} in Claude, ChatGPT or Gemini</h2>
              <p className={`text-sm ${muted}`}>
                Add AnalystBro as a connector in the AI chat app you already use, then ask about {name}: its results,
                filings, dividends or chart.
              </p>
              <code className="font-mono text-xs rounded-lg border border-[var(--ab-border)] bg-[var(--ab-bg-alt)] px-3 py-1.5 w-fit">
                https://mcp.analystbro.net/mcp
              </code>
            </div>
            <Link to="/settings?tab=ai-apps" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--ab-accent)] px-5 min-h-[44px] font-semibold text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)] shrink-0">
              Get your connection details <ArrowRight size={16} />
            </Link>
          </section>
          <p className={`text-xs ${muted}`}>
            Data may be delayed. Prices, statistics and financial statements are standardised and may differ from the
            figures in a company's own reports; the reports are the reference. Not investment advice.
          </p>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className={`${THEME} min-h-screen flex flex-col font-sans antialiased`}>
      <AnalystBroNavbar theme={THEME} />
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-8">{children}</main>
      <AnalystBroFooter theme={THEME} />
    </div>
  );
}
